import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { CityOption } from '../../services/locationsApi';

type CityAutocompleteProps = {
  cities: CityOption[];
  cityId?: string;
  disabled?: boolean;
  onSelect: (city: CityOption | null) => void;
};

const RESULT_LIMIT = 40;

function normalize(value: string): string {
  return value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();
}

export function CityAutocomplete({
  cities,
  cityId,
  disabled = false,
  onSelect,
}: CityAutocompleteProps) {
  const listId = useId();
  const rootRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');

  const selected = cities.find((city) => city.id === cityId);

  useEffect(() => {
    setQuery(selected?.name ?? '');
  }, [selected?.id, selected?.name]);

  useEffect(() => {
    function handlePointerDown(event: MouseEvent): void {
      if (!rootRef.current?.contains(event.target as Node)) {
        setOpen(false);
        setQuery(selected?.name ?? '');
      }
    }

    document.addEventListener('mousedown', handlePointerDown);
    return () => document.removeEventListener('mousedown', handlePointerDown);
  }, [selected?.name]);

  const { matches, truncated } = useMemo(() => {
    const term = normalize(query.trim());
    const selectedName = normalize(selected?.name ?? '');
    const searching = term.length > 0 && term !== selectedName;
    const source = searching
      ? cities.filter((city) => normalize(city.name).includes(term))
      : cities;

    return {
      matches: source.slice(0, RESULT_LIMIT),
      truncated: source.length > RESULT_LIMIT,
    };
  }, [cities, query, selected?.name]);

  function choose(city: CityOption): void {
    onSelect(city);
    setQuery(city.name);
    setOpen(false);
  }

  function clear(): void {
    onSelect(null);
    setQuery('');
    setOpen(true);
  }

  return (
    <div ref={rootRef} className="relative">
      <input
        className="erp-input"
        role="combobox"
        aria-expanded={open}
        aria-controls={listId}
        aria-autocomplete="list"
        value={query}
        placeholder={disabled ? 'Selecione o estado' : 'Busque pelo nome da cidade'}
        disabled={disabled}
        autoComplete="off"
        onFocus={() => setOpen(true)}
        onChange={(event) => {
          setQuery(event.target.value);
          setOpen(true);
          if (!event.target.value.trim()) {
            onSelect(null);
          }
        }}
      />
      {open && !disabled ? (
        <ul
          id={listId}
          role="listbox"
          className="absolute z-20 mt-1 max-h-60 w-full overflow-auto rounded-lg border border-secondary-200 bg-white py-1 shadow-lg"
        >
          {cityId ? (
            <li>
              <button
                type="button"
                className="w-full px-3 py-2 text-left text-sm text-secondary-500 hover:bg-secondary-50"
                onMouseDown={(event) => event.preventDefault()}
                onClick={clear}
              >
                Sem cidade
              </button>
            </li>
          ) : null}
          {cities.length === 0 ? (
            <li className="px-3 py-2 text-sm text-secondary-500">Nenhuma cidade neste estado.</li>
          ) : matches.length === 0 ? (
            <li className="px-3 py-2 text-sm text-secondary-500">Nenhuma cidade encontrada.</li>
          ) : (
            matches.map((city) => (
              <li key={city.id}>
                <button
                  type="button"
                  role="option"
                  aria-selected={city.id === cityId}
                  className={`w-full px-3 py-2 text-left text-sm hover:bg-secondary-50 ${
                    city.id === cityId ? 'bg-primary-50 text-primary-800' : 'text-secondary-800'
                  }`}
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() => choose(city)}
                >
                  {city.name}
                </button>
              </li>
            ))
          )}
          {truncated ? (
            <li className="px-3 py-2 text-xs text-secondary-500">
              Continue digitando para refinar a busca.
            </li>
          ) : null}
        </ul>
      ) : null}
    </div>
  );
}
